import type {
  Metadata,
  Pane,
  PaneUpdate,
  PaneUpdatedAction,
  PanelName,
  PanelUpdateMeta,
  Parameters,
} from './types';
import {first} from './utils';

export const DIMENSIONS_UPDATED = '__PANELS_INTERNAL__DIMENSIONS_UPDATED';
export const PANE_CLOSED = '__PANELS_INTERNAL__PANE_CLOSED';
export const PANEL_CHILDREN_CLOSED = '__PANELS_INTERNAL__PANEL_CHILDREN_CLOSED';
export const PANEL_CLOSED = '__PANELS_INTERNAL__PANEL_CLOSED';
export const PANEL_DOCK_TOGGLED = '__PANELS_INTERNAL__PANEL_DOCK_TOGGLED';
export const PANEL_OPENED = '__PANELS_INTERNAL__PANEL_OPENED';
export const PANEL_SHORTCUT_PRESSED =
  '__PANELS_INTERNAL__PANEL_SHORTCUT_PRESSED';
export const PANEL_TOGGLED = '__PANELS_INTERNAL__PANEL_TOGGLED';
export const PENDING_ACTION_REJECTED =
  // adding as const hack allows us to generate flow-typed/npm/@packages/systems/panels/index.js.flow without loosing types
  '__PANELS_INTERNAL__PENDING_ACTION_REJECTED' as const;
export const REJECTED_TRANSITION_UNBLOCKED =
  '__PANELS_INTERNAL__REJECTED_TRANSITION_UNBLOCKED';

// adding as const hack allows us to generate flow-typed/npm/@packages/systems/panels/index.js.flow without loosing types
export const PANE_UPDATED = 'PANE_UPDATED' as const;
export const PANELS_REFRESHED = 'PANELS_REFRESHED';
export const PANELS_MODE_UPDATED = 'PANELS_MODE_UPDATED';

type ActionWithPayload<Type, Payload> = {
  type: Type;
  payload: Payload;
};
type Action<Type> = {
  type: Type;
};

type PaneClosed = ActionWithPayload<
  typeof PANE_CLOSED,
  {
    pane: Pane;
    metadata: Metadata;
  }
>;
export const paneClosed = (
  pane: Pane,
  metadata: Metadata = {}
): PaneClosed => ({
  type: PANE_CLOSED,
  payload: {metadata, pane},
});

type PanelChildrenClosed = ActionWithPayload<
  typeof PANEL_CHILDREN_CLOSED,
  {
    panelName: PanelName;
    metadata: Metadata;
  }
>;
export const panelChildrenClosed = (
  panelName: PanelName,
  metadata: Metadata = {}
): PanelChildrenClosed => ({
  type: PANEL_CHILDREN_CLOSED,
  payload: {metadata, panelName},
});

type PanelClosed = ActionWithPayload<
  typeof PANEL_CLOSED,
  {
    panelName: PanelName;
    metadata: Metadata;
  }
>;
export const panelClosed = (
  panelName: PanelName,
  metadata: Metadata = {}
): PanelClosed => ({
  type: PANEL_CLOSED,
  payload: {metadata, panelName},
});

type PanelOpened = ActionWithPayload<
  typeof PANEL_OPENED,
  {
    panelName: PanelName;
    parameters: Parameters | null | undefined;
    metadata: Metadata;
  }
>;
export const panelOpened = (
  panelName: PanelName,
  parameters?: Parameters,
  metadata: Metadata = {}
): PanelOpened => ({
  type: PANEL_OPENED,
  payload: {metadata, panelName, parameters},
});

type PanelDockToggled = ActionWithPayload<
  typeof PANEL_DOCK_TOGGLED,
  {
    panelName: PanelName;
    metadata: Metadata;
  }
>;
export const panelDockToggled = (
  panelName: PanelName,
  metadata: Metadata = {}
): PanelDockToggled => ({
  type: PANEL_DOCK_TOGGLED,
  payload: {metadata, panelName},
});

type PanelToggled = ActionWithPayload<
  typeof PANEL_TOGGLED,
  {
    panelName: PanelName;
    metadata: Metadata;
  }
>;
export const panelToggled = (
  panelName: PanelName,
  metadata: Metadata = {}
): PanelToggled => ({
  type: PANEL_TOGGLED,
  payload: {metadata, panelName},
});

type PanelShortcutPressed = ActionWithPayload<
  typeof PANEL_SHORTCUT_PRESSED,
  {
    panelName: PanelName;
    metadata: Metadata;
  }
>;
export const panelShortcutPressed = (
  panelName: PanelName,
  metadata: Metadata = {}
): PanelShortcutPressed => ({
  type: PANEL_SHORTCUT_PRESSED,
  payload: {metadata, panelName},
});

type DimensionsUpdated = ActionWithPayload<
  typeof DIMENSIONS_UPDATED,
  {
    windowWidth: number;
    metadata: Metadata;
  }
>;
export const dimensionsUpdated = (
  windowWidth: number,
  metadata: Metadata = {}
): DimensionsUpdated => ({
  type: DIMENSIONS_UPDATED,
  payload: {metadata, windowWidth},
});

type PanelsRefreshed = Action<typeof PANELS_REFRESHED>;
export const panelsRefreshed = (): PanelsRefreshed => ({
  type: PANELS_REFRESHED,
});

type RejectedTransitionUnblocked = Action<typeof REJECTED_TRANSITION_UNBLOCKED>;
export const rejectedTransitionUnblocked = (): RejectedTransitionUnblocked => ({
  type: REJECTED_TRANSITION_UNBLOCKED,
});

export type PanelAction =
  | PaneClosed
  | PanelClosed
  | PanelOpened
  | PanelChildrenClosed
  | PanelDockToggled
  | PanelToggled
  | PanelShortcutPressed
  | DimensionsUpdated
  | PanelsRefreshed
  | RejectedTransitionUnblocked;

// IMPORTANT NOTE:
// The paneUpdated action creator should never be dispatched outside of the
// PanelsEngine. If we were to dispatch it directly, the lifecycle methods
// of the transition panels would not be called, so panels would
// not be able to prevent entry or exit.

export function isPaneUpdatedAction(action: {
  type: string;
}): action is PaneUpdatedAction {
  return action.type === PANE_UPDATED;
}

export const paneUpdated = (
  panels: PanelUpdateMeta,
  metadata: Metadata = {},
  ...updates: Array<PaneUpdate>
): Omit<PaneUpdatedAction, 'state'> => ({
  type: PANE_UPDATED,
  payload: {panels, updates, metadata},
});

// However, this action is also the only panel system action which is
// safe for the rest of the app to react to directly, because it's
// the only one which guarantees that an update will happen. To
// help support this, we provide some selectors to help derive
// useful information from the action's payload.

export const paneUpdateRootPaths = (
  action: PaneUpdatedAction
): Partial<Record<PanelName, boolean>> =>
  action.payload.updates.reduce<Record<string, any>>(
    (acc, {path, isOpen}) =>
      path
        ? Object.assign(acc, {
            // @ts-expect-error - TS2464 - A computed property name must be of type 'string', 'number', 'symbol', or 'any'.
            [path[0]]: typeof isOpen === 'boolean' ? isOpen : true,
          })
        : acc,
    {}
  );

export const paneUpdateStartsWith = (
  action: PaneUpdatedAction,
  panelName: PanelName
): boolean =>
  action.payload.updates.some(({path}) => first(path) === panelName);

export const paneIsUpdateTarget = (
  action: PaneUpdatedAction,
  target: Pane
): boolean => action.payload.updates.some(({pane}) => pane === target);

export const paneUpdateRootPath = (
  action: PaneUpdatedAction,
  target: Pane
): PanelName | null => {
  const {path} = action.payload.updates.find(({pane}) => pane === target) || {};

  // @ts-expect-error - TS2322 - Type 'string | null | undefined' is not assignable to type 'string | null'.
  return first(path);
};

export const isPanelClosing = (
  action: PaneUpdatedAction,
  target: PanelName
): boolean => Boolean(action.payload.panels.closing[target]);

export const isPanelOpening = (
  action: PaneUpdatedAction,
  target: PanelName
): boolean => Boolean(action.payload.panels.opening[target]);

// Determine if the panel is either opening or closing
export const isPanelToggling = (
  action: PaneUpdatedAction,
  target: PanelName
): boolean => isPanelOpening(action, target) || isPanelClosing(action, target);

// This is also likely to be for internal use only, but does not have
// the gotcha so could be used externally without problems if needed.
// However, `cancelTogglePanel` with more limited behaviour would
// probably be preferable if it's needed.
export const pendingActionRejected = (redirectTo?: {
  panelName: string;
  parameters?: Parameters;
}) => ({
  type: PENDING_ACTION_REJECTED,
  payload: {redirectTo},
});
